import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { Shield, Loader2, Check, Zap, Eye, EyeOff } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { ForgotPasswordDialog } from '@/components/ForgotPasswordDialog';
import { PasswordInput } from '@/components/ui/password-input';
import { ValidatedInput } from '@/components/ui/validated-input';
import { useFormValidation } from '@/hooks/useFormValidation';
import { signInSchema, signUpSchema } from '@/lib/validation';
import { logSecurityEvent, SECURITY_CONFIG } from '@/lib/security';

const Auth = () => {
  const { signIn, signUp, user, session } = useAuth();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  
  // Check URL params for initial tab
  const getInitialTab = () => {
    const tab = searchParams.get('tab');
    return tab === 'signup' ? 'signup' : 'signin';
  };
  
  const [activeTab, setActiveTab] = useState(getInitialTab());
  const [showPassword, setShowPassword] = useState(false);
  const [inviteInfo, setInviteInfo] = useState<{
    organization_name: string;
    email: string;
    token: string;
  } | null>(null);
  const [organizationValidation, setOrganizationValidation] = useState<{
    isValid: boolean;
    message: string;
  }>({ isValid: true, message: '' });
  const [checkingOrganization, setCheckingOrganization] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  
  // Sign In form state
  const [signInData, setSignInData] = useState({
    email: '',
    password: '',
  });

  // Sign Up form state
  const [signUpData, setSignUpData] = useState({
    email: '',
    password: '',
    displayName: '',
    organizationName: '',
  });

  useEffect(() => {
    const handleInvitation = async () => {
      if (window.location.hash.includes("access_token")) {
        // Extract tokens from hash fragment
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        
        if (accessToken && refreshToken) {
          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          });

          if (error) {
            toast({
              title: "Invitation link invalid",
              description: error.message || "This invitation link has expired or is invalid.",
              variant: "destructive",
            });
            return;
          }

          window.history.replaceState(
            {},
            document.title,
            window.location.pathname + window.location.search
          );

          if (data.session?.user?.email) {
            const orgName = data.session.user.user_metadata?.organization_name || "";
            const orgParam = new URLSearchParams(window.location.search).get("org");
            const finalOrgName = orgName || orgParam || "";
            
            setInviteInfo({
              organization_name: finalOrgName,
              email: data.session.user.email,
              token: "session",
            });

            setSignUpData(prev => ({
              ...prev,
              email: data.session.user.email,
              organizationName: finalOrgName,
            }));

            setActiveTab("signup");

            toast({
              title: "Welcome!",
              description: finalOrgName
                ? `You've been invited to join ${finalOrgName}. Complete your signup below.`
                : "Complete your signup to get started.",
            });
          }
        }
      }
    };

    handleInvitation();
  }, []);

  // Navigate to trial options for new users, admin for existing users
  useEffect(() => {
    if (user && session) {
      // Check if this is a new signup by looking for a URL parameter or checking user metadata
      const urlParams = new URLSearchParams(window.location.search);
      const isNewSignup = urlParams.get('new_signup') === 'true';
      
      if (isNewSignup) {
        window.location.href = '/trial-options';
      } else {
        window.location.href = '/admin';
      }
    }
  }, [user, session]);


  const validateOrganizationName = async (organizationName: string) => {
    if (!organizationName || organizationName.trim() === '') {
      setOrganizationValidation({ isValid: true, message: '' });
      return;
    }

    // Basic client-side validation first
    const trimmedName = organizationName.trim();
    if (trimmedName.length < 2) {
      setOrganizationValidation({ 
        isValid: false, 
        message: 'Organization name must be at least 2 characters long' 
      });
      return;
    }
    
    if (trimmedName.length > 100) {
      setOrganizationValidation({ 
        isValid: false, 
        message: 'Organization name must be less than 100 characters' 
      });
      return;
    }

    setCheckingOrganization(true);
    try {
      // Try the RPC function first
      const { data, error } = await supabase
        .rpc('check_organization_name_availability', { org_name: trimmedName });

      if (error) {
        console.warn('RPC validation failed, using basic validation:', error);
        // Fall back to basic validation if RPC fails
        setOrganizationValidation({ 
          isValid: true, 
          message: 'Organization name format looks good' 
        });
        return;
      }

      if (data && data.length > 0) {
        const result = data[0];
        setOrganizationValidation({ 
          isValid: result.is_available, 
          message: result.message 
        });
      } else {
        // Fallback if no data returned
        setOrganizationValidation({ 
          isValid: true, 
          message: 'Organization name format looks good' 
        });
      }
    } catch (error) {
      console.warn('Organization validation error, using fallback:', error);
      // Fallback to basic client-side validation
      setOrganizationValidation({ 
        isValid: true, 
        message: 'Organization name format looks good' 
      });
    } finally {
      setCheckingOrganization(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await signIn(signInData.email, signInData.password);
      
      if (error) {
        toast({
          title: 'Sign in failed',
          description: error.message || 'Please check your credentials and try again.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Sign in failed',
        description: 'An unexpected error occurred. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (inviteInfo) {
        // 🟢 Invited user flow
        const { error } = await supabase.auth.updateUser({
          password: signUpData.password,
          data: {
            display_name: signUpData.displayName,
            organization_name: inviteInfo.organization_name, // persist org
          },
        });

        if (error) {
          toast({
            title: "Account setup failed",
            description: error.message || "Please try again.",
            variant: "destructive",
          });
          return;
        }

        toast({
          title: "Welcome to the team!",
          description: `You've successfully joined ${inviteInfo.organization_name}.`,
        });

        // Redirect straight to dashboard
        window.location.href = "/admin";

      } else {
        // 🟢 Normal self-signup flow
        const { error, data } = await signUp(
          signUpData.email,
          signUpData.password,
          signUpData.displayName,
          undefined,
          signUpData.organizationName
        );

        if (error) {
          toast({
            title: "Sign up failed",
            description: error.message || "Please check your information and try again.",
            variant: "destructive",
          });
        } else {
          if (data?.emailConfirmationRequired) {
            toast({
              title: "Account created successfully",
              description: "Please check your email to confirm your account.",
            });
          } else {
            toast({
              title: "Account created successfully",
              description: "Welcome! You have been signed in automatically.",
            });

            // Mark as new signup for redirect
            const url = new URL(window.location.href);
            url.searchParams.set("new_signup", "true");
            window.history.replaceState({}, "", url.toString());
            // redirect handled by useEffect
          }
        }
      }
    } catch (error) {
      toast({
        title: "Sign up failed",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="min-h-screen bg-background">
      <div className="grid lg:grid-cols-6 min-h-screen">
        {/* Left Column - Auth Form (Dominant) */}
        <div className="lg:col-span-5 flex items-center justify-center p-4 lg:p-8">
          <Card className="w-full max-w-xl shadow-lg">
            <CardHeader className="text-center pb-8">
              <div className="flex items-center justify-center space-x-2 mb-4">
                <Shield className="h-8 w-8 text-primary" />
                <span className="text-2xl font-bold text-foreground">SolidWraps</span>
              </div>
              <CardDescription className="text-base">
                Sign in to your account or create a new one
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-8">
                  <TabsTrigger value="signin">Sign In</TabsTrigger>
                  <TabsTrigger value="signup">Sign Up</TabsTrigger>
                </TabsList>

                <TabsContent value="signin">
                  <form onSubmit={handleSignIn} className="space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="signin-email">Email</Label>
                      <Input
                        id="signin-email"
                        type="email"
                        placeholder="Enter your email"
                        value={signInData.email}
                        onChange={(e) =>
                          setSignInData({ ...signInData, email: e.target.value })
                        }
                        required
                        className="h-12"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signin-password">Password</Label>
                      <div className="relative">
                        <Input
                          id="signin-password"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Enter your password"
                          value={signInData.password}
                          onChange={(e) =>
                            setSignInData({ ...signInData, password: e.target.value })
                          }
                          required
                          className="h-12"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute right-0 top-0 h-full px-3"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                    <Button type="submit" className="w-full h-12 text-base" disabled={loading}>
                      {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Sign In
                    </Button>
                    <div className="text-center">
                      <Button
                        type="button"
                        variant="link"
                        className="text-sm text-muted-foreground"
                        onClick={() => setShowForgotPassword(true)}
                      >
                        Forgot your password?
                      </Button>
                    </div>
                  </form>
                </TabsContent>

                <TabsContent value="signup">
                  <form onSubmit={handleSignUp} className="space-y-6">
                    <ValidatedInput
                      id="signup-name"
                      type="text"
                      label="Display Name"
                      placeholder="Enter your display name"
                      value={signUpData.displayName}
                      onChange={(value) =>
                        setSignUpData({ ...signUpData, displayName: value })
                      }
                      validationType="displayName"
                      validateOnChange={true}
                      required
                      className="h-12"
                    />
                    
                    <ValidatedInput
                      id="signup-organization"
                      type="text"
                      label={`Organization Name ${inviteInfo ? '(from invite)' : ''}`}
                      placeholder={inviteInfo ? inviteInfo.organization_name : "Enter your organization name"}
                      value={inviteInfo ? inviteInfo.organization_name : signUpData.organizationName}
                      onChange={(value) => {
                        setSignUpData({ ...signUpData, organizationName: value });
                        if (!inviteInfo && value) {
                          validateOrganizationName(value);
                        }
                      }}
                      validationType="organizationName"
                      validateOnChange={!inviteInfo}
                      disabled={!!inviteInfo}
                      required={!inviteInfo}
                      className={`h-12 ${inviteInfo ? 'bg-muted' : ''}`}
                    />
                    
                    {!inviteInfo && signUpData.organizationName && (
                      <div className="text-xs">
                        {checkingOrganization ? (
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Loader2 className="h-3 w-3 animate-spin" />
                            <span>Checking availability...</span>
                          </div>
                        ) : (
                          <div className={`flex items-center gap-2 ${organizationValidation.isValid ? 'text-green-600' : 'text-destructive'}`}>
                            {organizationValidation.isValid ? (
                              <Check className="h-3 w-3" />
                            ) : (
                              <Shield className="h-3 w-3" />
                            )}
                            <span>{organizationValidation.message}</span>
                          </div>
                        )}
                      </div>
                    )}

                    <ValidatedInput
                      id="signup-email"
                      type="email"
                      label={`Email ${inviteInfo ? '(from invite)' : ''}`}
                      placeholder="Enter your email"
                      value={signUpData.email}
                      onChange={(value) =>
                        setSignUpData({ ...signUpData, email: value })
                      }
                      validationType="email"
                      validateOnChange={true}
                      disabled={!!inviteInfo}
                      required
                      className={`h-12 ${inviteInfo ? 'bg-muted' : ''}`}
                    />

                    <PasswordInput
                      id="signup-password"
                      label="Password"
                      placeholder="Create a secure password"
                      value={signUpData.password}
                      onChange={(value) =>
                        setSignUpData({ ...signUpData, password: value })
                      }
                      showStrengthMeter={true}
                      required
                      className="h-12"
                    />

                    <Button 
                      type="submit" 
                      className="w-full h-12 text-base" 
                      disabled={loading || (!inviteInfo && !organizationValidation.isValid && signUpData.organizationName !== '')}
                    >
                      {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      {inviteInfo ? 'Join Organization' : 'Create Account'}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Marketing Content (Secondary) */}
        <div className="lg:col-span-1 hidden lg:flex bg-gradient-to-br from-primary/5 to-primary/10 items-center justify-center p-4">
          <div className="space-y-6 text-center">
            <div className="space-y-3">
              <h2 className="text-xl font-bold text-foreground">
                Secure Consent Management
              </h2>
              <p className="text-sm text-muted-foreground">
                Built for compliance and legal peace of mind
              </p>
            </div>

            <div className="space-y-4">
              <div className="flex flex-col items-center space-y-2">
                <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                  <Shield className="w-5 h-5 text-primary-foreground" />
                </div>
                <div className="text-center">
                  <h3 className="text-sm font-semibold text-foreground">Legal Ready</h3>
                  <p className="text-xs text-muted-foreground">Tamper-proof audit trails</p>
                </div>
              </div>

              <div className="flex flex-col items-center space-y-2">
                <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                  <Zap className="w-5 h-5 text-primary-foreground" />
                </div>
                <div className="text-center">
                  <h3 className="text-sm font-semibold text-foreground">Quick Deploy</h3>
                  <p className="text-xs text-muted-foreground">Live in minutes</p>
                </div>
              </div>

              <div className="flex flex-col items-center space-y-2">
                <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                  <Check className="w-5 h-5 text-primary-foreground" />
                </div>
                <div className="text-center">
                  <h3 className="text-sm font-semibold text-foreground">Compliant</h3>
                  <p className="text-xs text-muted-foreground">GDPR & CCPA ready</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <ForgotPasswordDialog 
        open={showForgotPassword} 
        onOpenChange={setShowForgotPassword} 
      />
    </div>
  );
};

export default Auth;